import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  LinearProgress,
} from "components/mui";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { ArrowForwardIcon, ArrowBackIcon } from "components/icons";
import SearchIcon from '@mui/icons-material/Search';
import { getComparator, stableSort } from "libs/sortList";
import {
  getListContacts,
  CreateList,
  updateMailingList,
  getCompanyContacts,
} from "api/canmail/CANmailAPI";
import getContacts from "api/contacts/getContacts";
import getCompanies from "api/companies/getCompanies";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CleanListModal from "./CleanListModal";

// Constants
const LIST_TYPE_ID = 1;
const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

// Utility functions for contact field access
const getContactId = (contact) => contact.id || contact.contact_id;

const getContactEmail = (contact) => 
  contact.email_work || contact.email_address || contact.emailWork || contact.WorkEmail || contact.email;

const getContactFirstName = (contact) => 
  contact.first_name || contact.firstName || contact.FirstName || "";

const getContactLastName = (contact) => 
  contact.last_name || contact.lastName || contact.LastName || "";

const getContactDisplayName = (contact) => {
  const firstName = getContactFirstName(contact);
  const lastName = getContactLastName(contact);
  return `${firstName} ${lastName}`.trim() || "Unnamed Contact";
};

// Contact filtering and sorting utilities
const filterContactsByEmail = (contacts) => 
  contacts.filter(contact => getContactEmail(contact));

const filterContactsByExclusion = (contacts, excludeContacts) => {
  if (!excludeContacts.length) return contacts;
  
  const excludeIds = new Set(excludeContacts.map(getContactId));
  const excludeEmails = new Set(
    excludeContacts
      .map(getContactEmail)
      .filter(Boolean)
      .map(email => email.toLowerCase())
  );
  
  return contacts.filter(contact => {
    const contactId = getContactId(contact);
    const contactEmail = getContactEmail(contact);
    
    // Check by ID first
    if (excludeIds.has(contactId)) return false;
    
    // Check by email as fallback
    if (contactEmail && excludeEmails.has(contactEmail.toLowerCase())) return false;
    
    return true;
  });
};

const filterContactsBySearch = (contacts, searchTerm) => {
  if (!searchTerm.trim()) return contacts;
  
  const searchLower = searchTerm.toLowerCase();
  return contacts.filter(contact => {
    const firstName = getContactFirstName(contact);
    const lastName = getContactLastName(contact);
    const email = getContactEmail(contact);
    
    return (
      firstName.toLowerCase().includes(searchLower) ||
      lastName.toLowerCase().includes(searchLower) ||
      (email && email.toLowerCase().includes(searchLower))
    );
  });
};

const ContactTable = ({
  contacts,
  title,
  onMoveContacts,
  moveButtonText,
  moveButtonIcon,
  orderBy,
  order,
  onSort,
  loading = false,
  showSearch = false,
  excludeContacts = [],
  showCompanyFilter = false,
  companies = [],
  selectedCompany = "",
  onCompanyChange = null,
}) => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Memoized contact comparator
  const contactComparator = useCallback((a, b) => {
    let aValue, bValue;

    switch (orderBy) {
      case "firstName":
        aValue = getContactFirstName(a).toLowerCase();
        bValue = getContactFirstName(b).toLowerCase();
        break;
      case "lastName":
        aValue = getContactLastName(a).toLowerCase();
        bValue = getContactLastName(b).toLowerCase();
        break;
      case "email":
        aValue = (getContactEmail(a) || "").toLowerCase();
        bValue = (getContactEmail(b) || "").toLowerCase();
        break;
      default:
        aValue = "";
        bValue = "";
    }

    return order === "desc" 
      ? bValue.localeCompare(aValue)
      : aValue.localeCompare(bValue);
  }, [orderBy, order]);

  // Memoized filtered and sorted contacts
  const filteredContacts = useMemo(() => {
    let filtered = filterContactsByEmail(contacts);
    filtered = filterContactsByExclusion(filtered, excludeContacts);
    filtered = filterContactsBySearch(filtered, searchTerm);
    return stableSort(filtered, contactComparator);
  }, [contacts, excludeContacts, searchTerm, contactComparator]);

  // Reset visible count and selection when contacts change
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    setSelectedContacts([]);
  }, [contacts, orderBy, order, searchTerm]);

  // Infinite scroll logic
  const handleScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    if (
      scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD &&
      !isLoadingMore &&
      visibleCount < filteredContacts.length
    ) {
      setIsLoadingMore(true);

      setTimeout(() => {
        setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, filteredContacts.length));
        setIsLoadingMore(false);
      }, 100);
    }
  }, [isLoadingMore, visibleCount, filteredContacts.length]);

  // Get visible contacts
  const visibleContacts = filteredContacts.slice(0, visibleCount);

  // Selection handlers
  const handleSelectContact = useCallback((contact) => {
    const contactId = getContactId(contact);
    setSelectedContacts(prev => {
      if (prev.some(c => getContactId(c) === contactId)) {
        return prev.filter(c => getContactId(c) !== contactId);
      } else {
        return [...prev, contact];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedContacts.length === visibleContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts([...visibleContacts]);
    }
  }, [selectedContacts.length, visibleContacts]);

  const isContactSelected = useCallback((contact) => {
    const contactId = getContactId(contact);
    return selectedContacts.some(c => getContactId(c) === contactId);
  }, [selectedContacts]);

  const handleMoveSelected = useCallback(() => {
    if (selectedContacts.length > 0) {
      onMoveContacts(selectedContacts);
      setSelectedContacts([]);
    }
  }, [selectedContacts, onMoveContacts]);

  // Memoized table cell styles
  const tableCellStyles = useMemo(() => ({
    checkbox: { minWidth: { xs: 40, sm: 50 } },
    name: { 
      minWidth: { xs: 80, sm: 100 },
      fontSize: { xs: '0.75rem', sm: '0.875rem' }
    },
    email: { 
      minWidth: { xs: 120, sm: 150 },
      fontSize: { xs: '0.75rem', sm: '0.875rem' }
    }
  }), []);

  return (
    <Paper
      elevation={1}
      sx={{ 
        height: "100%", 
        display: "flex", 
        flexDirection: "column",
        minHeight: { xs: 400, sm: 500 }
      }}
    >
      <Box sx={{ p: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: "divider" }}>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "flex-start", 
          mb: 1,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 }
        }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {title}
          </Typography>
          <Box sx={{ 
            display: "flex", 
            gap: 2, 
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" }
          }}>
            {showCompanyFilter && (
              <FormControl size="small" sx={{ 
                minWidth: { xs: "100%", sm: 200 },
                width: { xs: "100%", sm: "auto" }
              }}>
                <InputLabel>Filter by Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={(e) => onCompanyChange?.(e.target.value)}
                  label="Filter by Company"
                >
                  <MenuItem value="">
                    <em>All Companies</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.Id} value={company.Id}>
                      {company.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {showSearch && (
              <TextField
                size="small"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ 
                  width: { xs: "100%", sm: 200 },
                  minWidth: { xs: "100%", sm: 200 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {filteredContacts.length} {filteredContacts.length === 1 ? "contact" : "contacts"}
        </Typography>
      </Box>
      
      <TableContainer 
        sx={{ 
          flex: 1, 
          maxHeight: { xs: 300, sm: 500 },
          overflowX: "auto"
        }} 
        onScroll={handleScroll}
      >
        <Table size="small" sx={{ minWidth: { xs: 400, sm: 600 } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={tableCellStyles.checkbox}>
                <Checkbox
                  indeterminate={selectedContacts.length > 0 && selectedContacts.length < visibleContacts.length}
                  checked={selectedContacts.length > 0 && selectedContacts.length === visibleContacts.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={tableCellStyles.name}>
                <TableSortLabel
                  active={orderBy === "firstName"}
                  direction={orderBy === "firstName" ? order : "asc"}
                  onClick={() => onSort("firstName")}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={tableCellStyles.name}>
                <TableSortLabel
                  active={orderBy === "lastName"}
                  direction={orderBy === "lastName" ? order : "asc"}
                  onClick={() => onSort("lastName")}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={tableCellStyles.email}>
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? order : "asc"}
                  onClick={() => onSort("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box sx={{ width: '100%', py: 2 }}>
                    <LinearProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No contacts found
                </TableCell>
              </TableRow>
            ) : (
              visibleContacts.map((contact) => (
                <TableRow 
                  key={getContactId(contact)} 
                  hover
                  onClick={() => handleSelectContact(contact)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={tableCellStyles.checkbox}>
                    <Checkbox
                      checked={isContactSelected(contact)}
                      onChange={() => handleSelectContact(contact)}
                    />
                  </TableCell>
                  <TableCell sx={tableCellStyles.name}>
                    {getContactFirstName(contact) || "No First Name"}
                  </TableCell>
                  <TableCell sx={tableCellStyles.name}>
                    {getContactLastName(contact) || "No Last Name"}
                  </TableCell>
                  <TableCell sx={tableCellStyles.email}>
                    {getContactEmail(contact) ? (
                      <Chip
                        label={getContactEmail(contact)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        No Email
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ p: { xs: 1, sm: 2 }, borderTop: 1, borderColor: "divider" }}>
        <Button
          variant="contained"
          startIcon={moveButtonIcon}
          onClick={handleMoveSelected}
          disabled={selectedContacts.length === 0}
          fullWidth
          sx={{ 
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            py: { xs: 1, sm: 1.5 }
          }}
        >
          {moveButtonText} {selectedContacts.length > 0 && `(${selectedContacts.length})`}
        </Button>
      </Box>
      
      <Box sx={{ p: { xs: 0.5, sm: 1 }, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
          Showing {visibleContacts.length} of {filteredContacts.length} contacts
        </Typography>
      </Box>
    </Paper>
  );
};

const MailingListEditor = ({ list, setMailingLists, onClose, onListUpdated }) => {
  const [listName, setListName] = useState(list?.list_name || "");
  const [allContacts, setAllContacts] = useState([]);
  const [listContacts, setListContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState("firstName");
  const [order, setOrder] = useState("asc");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [showCleanModal, setShowCleanModal] = useState(false);

  // Load companies on component mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Load list contacts when editing existing list
  useEffect(() => {
    if (list) {
      loadListContacts();
    }
  }, [list]);

  // Load contacts when company selection changes
  useEffect(() => {
    if (companies.length > 0) {
      loadContacts();
    }
  }, [selectedCompany, companies.length]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      let contacts;
      
      if (selectedCompany) {
        contacts = await getCompanyContacts(selectedCompany, list?.list_id || 0, LIST_TYPE_ID);
      } else {
        contacts = await getContacts();
      }

      if (!contacts || !Array.isArray(contacts)) {
        setAllContacts([]);
        return;
      }

      setAllContacts(contacts);
    } catch (error) {
      console.error("Error loading contacts:", error);
      setAllContacts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, list?.list_id]);

  const loadListContacts = useCallback(async () => {
    if (!list) return;

    try {
      const contacts = await getListContacts(list.list_id, LIST_TYPE_ID);
      setListContacts(contacts && Array.isArray(contacts) ? contacts : []);
    } catch (error) {
      console.error("Error loading list contacts:", error);
      setListContacts([]);
    }
  }, [list]);

  const loadCompanies = useCallback(async () => {
    try {
      const companiesData = await getCompanies();
      setCompanies(companiesData && Array.isArray(companiesData) ? companiesData : []);
    } catch (error) {
      console.error("Error loading companies:", error);
      setCompanies([]);
    }
  }, []);

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }, [orderBy, order]);

  const moveContacts = useCallback((fromContacts, toContacts, contactsToMove) => {
    if (!contactsToMove?.length) return { from: fromContacts, to: toContacts };

    const moveIds = new Set(contactsToMove.map(getContactId));
    
    const newFromContacts = fromContacts.filter(contact => !moveIds.has(getContactId(contact)));
    const newToContacts = [...toContacts];
    
    contactsToMove.forEach(contact => {
      const contactId = getContactId(contact);
      if (!newToContacts.some(c => getContactId(c) === contactId)) {
        newToContacts.push(contact);
      }
    });

    return { from: newFromContacts, to: newToContacts };
  }, []);

  const moveContactsToRight = useCallback((selectedContacts) => {
    const { from, to } = moveContacts(allContacts, listContacts, selectedContacts);
    setAllContacts(from);
    setListContacts(to);
  }, [allContacts, listContacts, moveContacts]);

  const moveContactsToLeft = useCallback((selectedContacts) => {
    const { from, to } = moveContacts(listContacts, allContacts, selectedContacts);
    setListContacts(from);
    setAllContacts(to);
  }, [allContacts, listContacts, moveContacts]);

  const handleSave = useCallback(async () => {
    if (!listName.trim()) {
      alert("Please enter a list name");
      return;
    }

    setLoading(true);
    try {
      if (list) {
        // Update existing list
        const originalContacts = await getListContacts(list.list_id, LIST_TYPE_ID);
        const originalContactIds = new Set(originalContacts?.map(getContactId) || []);
        const currentContactIds = new Set(listContacts.map(getContactId));
        
        const toBeInsertedIds = listContacts
          .map(getContactId)
          .filter(id => !originalContactIds.has(id));
        
        const toBeDeletedIds = Array.from(originalContactIds)
          .filter(id => !currentContactIds.has(id));
        
        await updateMailingList(
          list.list_id,
          listName,
          LIST_TYPE_ID,
          JSON.stringify(toBeInsertedIds),
          JSON.stringify(toBeDeletedIds)
        );
        
        onListUpdated?.(list.list_id, listName);
      } else {
        // Create new list
        const contactIds = listContacts.map(getContactId);
        await CreateList(listName, LIST_TYPE_ID, JSON.stringify(contactIds));
      }

      onClose?.();
    } catch (error) {
      console.error("Error saving list:", error);
    } finally {
      setLoading(false);
    }
  }, [list, listName, listContacts, onListUpdated, onClose]);

  const handleCompanyChange = useCallback((companyId) => {
    setSelectedCompany(companyId);
  }, []);

  const handleCleaned = useCallback(() => {
    // Membership changed on the server; refresh the "Contacts in List" panel so the
    // removed hard-bounced contacts drop out of the editor immediately.
    loadListContacts();
  }, [loadListContacts]);

  const handleCancel = useCallback(() => {
    onClose?.();
  }, [onClose]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          {list ? "Edit Mailing List" : "Create New Mailing List"}
        </Typography>
        {list?.list_id && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CleaningServicesIcon />}
            onClick={() => setShowCleanModal(true)}
          >
            Clean
          </Button>
        )}
      </Box>

      <TextField
        fullWidth
        label="List Name"
        value={listName}
        onChange={(e) => setListName(e.target.value)}
        margin="normal"
        required
        sx={{ mb: 2 }}
      />

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <ContactTable
            contacts={allContacts}
            title="All Contacts"
            onMoveContacts={moveContactsToRight}
            moveButtonText="Add to List"
            moveButtonIcon={<ArrowForwardIcon />}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            loading={loading}
            showSearch={true}
            excludeContacts={listContacts}
            showCompanyFilter={true}
            companies={companies}
            selectedCompany={selectedCompany}
            onCompanyChange={handleCompanyChange}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <ContactTable
            contacts={listContacts}
            title="Contacts in List"
            onMoveContacts={moveContactsToLeft}
            moveButtonText="Remove from List"
            moveButtonIcon={<ArrowBackIcon />}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            loading={loading}
            showSearch={true}
          />
        </Grid>
      </Grid>

      <Box sx={{ 
        display: "flex", 
        gap: 2, 
        justifyContent: "flex-end",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" }
      }}>
        <Button 
          variant="outlined" 
          color="error" 
          onClick={handleCancel}
          fullWidth={false}
          sx={{ 
            order: { xs: 2, sm: 1 },
            minWidth: { xs: "auto", sm: "100px" }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !listName.trim()}
          fullWidth={false}
          sx={{ 
            order: { xs: 1, sm: 2 },
            minWidth: { xs: "auto", sm: "100px" }
          }}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </Box>

      {list?.list_id && (
        <CleanListModal
          open={showCleanModal}
          onClose={() => setShowCleanModal(false)}
          listId={list.list_id}
          listName={list.list_name || listName}
          onCleaned={handleCleaned}
        />
      )}
    </Box>
  );
};

// PropTypes for type checking
ContactTable.propTypes = {
  contacts: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  onMoveContacts: PropTypes.func.isRequired,
  moveButtonText: PropTypes.string.isRequired,
  moveButtonIcon: PropTypes.node.isRequired,
  orderBy: PropTypes.string.isRequired,
  order: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  showSearch: PropTypes.bool,
  excludeContacts: PropTypes.array,
  showCompanyFilter: PropTypes.bool,
  companies: PropTypes.array,
  selectedCompany: PropTypes.string,
  onCompanyChange: PropTypes.func,
};

MailingListEditor.propTypes = {
  list: PropTypes.object,
  setMailingLists: PropTypes.func,
  onClose: PropTypes.func,
  onListUpdated: PropTypes.func,
};

export default MailingListEditor;
